import { createClient, RealtimeChannel, RealtimePostgresChangesPayload, RealtimePostgresChangesFilter } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';
import { Token } from '../types/supabase';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://fuczyhbturoiyzposwkd.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'fallback-anon-key';
const supabaseServiceKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY || 'fallback-service-key';

// **🚀 HIGH-PERFORMANCE CONFIGURATION** 
const QUERY_TIMEOUT = 30000; // 30 seconds (increased from 15s)
const MAX_RETRIES = 5; // More retries for reliability
const INITIAL_RETRY_DELAY = 100; // Faster initial retry (reduced from 300ms)
const CONNECTION_POOL_SIZE = 20; // Larger pool for better performance
const REALTIME_THROTTLE = 50; // Reduced from 100 to prevent overload

// Enhanced retry delays for different error types
const RETRY_DELAYS = {
    network: 100,      // Network errors - retry quickly
    timeout: 500,      // Timeout errors - wait a bit longer
    server: 1000,      // Server errors - wait longer
    default: 300       // Default delay
};

// Check if Supabase is properly configured
const isSupabaseConfigured = supabaseUrl &&
    supabaseUrl !== 'https://fallback-url.supabase.co' &&
    supabaseAnonKey &&
    supabaseAnonKey !== 'fallback-anon-key' &&
    supabaseAnonKey.length > 20; // Ensure we have a real API key

// **🔧 USE ORIGINAL URL - REMOVED BROKEN POOLER OPTIMIZATION**
// The pooler URL format was incorrect and causing DNS errors
const optimizedSupabaseUrl = supabaseUrl; // Use original URL

// Use a singleton pattern to ensure only one instance of each client is created
let _supabase: ReturnType<typeof createClient<Database>> | null = null;
let _adminSupabase: ReturnType<typeof createClient<Database>> | null = null;
let _connectionHealth = {
    lastCheck: 0,
    isHealthy: true,
    consecutiveFailures: 0
};

// **🔧 MOCK CLIENT (moved before usage)**
const createMockClient = () => {
    return {
        from: (table: string) => ({
            select: () => ({
                range: () => ({
                    limit: () => ({
                        not: () => ({
                            order: () => ({
                                abortSignal: () => Promise.resolve({ data: [], error: null })
                            }),
                            eq: () => ({
                                order: () => ({
                                    abortSignal: () => Promise.resolve({ data: [], error: null })
                                })
                            })
                        }),
                        eq: () => ({
                            abortSignal: () => Promise.resolve({ data: [], error: null })
                        }),
                        textSearch: () => ({
                            abortSignal: () => Promise.resolve({ data: [], error: null })
                        }),
                        or: () => ({
                            order: () => ({
                                range: () => ({
                                    limit: () => ({
                                        abortSignal: () => Promise.resolve({ data: [], error: null })
                                    })
                                })
                            })
                        })
                    })
                })
            })
        }),
        channel: (name: string) => ({
            on: () => ({
                subscribe: () => ({
                    unsubscribe: () => { }
                })
            })
        }),
        auth: {
            onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => { } } } }),
            getSession: () => Promise.resolve({ data: { session: null }, error: null }),
            getUser: () => Promise.resolve({ data: { user: null }, error: null }),
            signOut: () => Promise.resolve({ error: null })
        }
    } as unknown as ReturnType<typeof createClient<Database>>;
};

// **🚀 ENHANCED RETRY LOGIC**
async function retryOperation<T>(
    operation: () => Promise<T>,
    context: string = 'unknown',
    maxRetries: number = MAX_RETRIES,
    getDelay: (attempt: number, error?: any) => number = (attempt, error) => {
        // Smart delay based on error type
        const errorType = error?.code || error?.message || '';
        let baseDelay = RETRY_DELAYS.default;

        if (errorType.includes('timeout') || errorType.includes('TIMEOUT')) {
            baseDelay = RETRY_DELAYS.timeout;
        } else if (errorType.includes('network') || errorType.includes('fetch')) {
            baseDelay = RETRY_DELAYS.network;
        } else if (errorType.includes('500') || errorType.includes('502') || errorType.includes('503')) {
            baseDelay = RETRY_DELAYS.server;
        }

        return Math.min(baseDelay * Math.pow(1.5, attempt - 1), 5000); // Cap at 5 seconds
    }
): Promise<T> {
    let lastError: any;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            const result = await operation();

            // Reset connection health on success
            if (_connectionHealth.consecutiveFailures > 0) {
                _connectionHealth.consecutiveFailures = 0;
                _connectionHealth.isHealthy = true;
                console.log('✅ Supabase connection recovered');
            }

            return result;
        } catch (error: any) {
            lastError = error;
            _connectionHealth.consecutiveFailures++;

            // Mark as unhealthy after 3 consecutive failures
            if (_connectionHealth.consecutiveFailures >= 3) {
                _connectionHealth.isHealthy = false;
            }

            // Don't retry certain errors
            if (error?.code === 'PGRST116' || // Row not found
                error?.code === '23505' ||    // Unique violation
                error?.message?.includes('duplicate key') ||
                error?.message?.includes('permission denied')) {
                throw error;
            }

            // Stop retrying if we've hit the limit
            if (attempt >= maxRetries) {
                console.error(`❌ Supabase operation failed after ${maxRetries} attempts [${context}]:`, error.message);
                break;
            }

            const delay = getDelay(attempt, error);
            console.warn(`🔄 Supabase retry attempt ${attempt}/${maxRetries} [${context}] in ${delay}ms:`, error.message);

            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }

    throw lastError;
}

// **🔧 OPTIMIZED CLIENT FACTORY**
const createOptimizedClient = (url: string, key: string, isAdmin: boolean = false) => {
    const clientConfig = {
        auth: {
            persistSession: !isAdmin, // Admin doesn't need session persistence
            storageKey: isAdmin ? 'ekko-admin-auth' : 'ekko-auth',
            autoRefreshToken: !isAdmin,
            detectSessionInUrl: !isAdmin,
            flowType: 'pkce' as const
        },
        global: {
            headers: {
                'x-application-name': 'ekko-trading-platform',
                'x-client-info': `supabase-js/${isAdmin ? 'admin' : 'client'}`,
                // Connection optimization headers
                'connection': 'keep-alive',
                'keep-alive': 'timeout=60'
            }
        }
    };

    return createClient<Database>(url, key, clientConfig);
};

// **🚀 HIGH-PERFORMANCE CLIENT INITIALIZATION**
export const supabase = (() => {
    if (!_supabase) {
        if (isSupabaseConfigured) {
            try {
                _supabase = createOptimizedClient(optimizedSupabaseUrl, supabaseAnonKey, false);
                console.log('✅ High-performance Supabase client initialized');
                console.log(`🔗 Using URL: ${optimizedSupabaseUrl}`);
            } catch (error) {
                console.error('❌ Error creating Supabase client:', error);
                _supabase = createMockClient();
            }
        } else {
            console.error('❌ Supabase not properly configured!');
            console.error('Missing environment variables:');
            if (!supabaseUrl || supabaseUrl === 'https://fallback-url.supabase.co') {
                console.error('  - VITE_SUPABASE_URL is missing or invalid');
            }
            if (!supabaseAnonKey || supabaseAnonKey === 'fallback-anon-key' || supabaseAnonKey.length <= 20) {
                console.error('  - VITE_SUPABASE_ANON_KEY is missing or invalid');
            }
            console.error('Please check your .env file and ensure all Supabase environment variables are set correctly.');
            _supabase = createMockClient();
        }
    }
    return _supabase;
})();

// Admin client with enhanced performance for admin operations
export const adminSupabase = (() => {
    if (!_adminSupabase) {
        if (isSupabaseConfigured) {
            try {
                _adminSupabase = createOptimizedClient(optimizedSupabaseUrl, supabaseServiceKey, true);
                console.log('✅ High-performance admin Supabase client initialized');
            } catch (error) {
                console.error('❌ Error creating admin Supabase client:', error);
                _adminSupabase = createMockClient();
            }
        } else {
            console.warn('⚠️ Supabase not configured. Using mock admin client.');
            _adminSupabase = createMockClient();
        }
    }
    return _adminSupabase;
})();

// **🔍 CONNECTION HEALTH MONITORING**
export const checkConnectionHealth = async (): Promise<boolean> => {
    try {
        const startTime = Date.now();
        const client = supabase;

        if (!client) {
            console.error('❌ Supabase client not initialized');
            _connectionHealth.isHealthy = false;
            return false;
        }

        // Simple health check query with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        try {
            const { error } = await client
                .from('new_tokens')
                .select('id', { count: 'exact', head: true })
                .limit(1);

            clearTimeout(timeoutId);

            if (error) {
                console.error('❌ Supabase health check failed:', error.message);
                _connectionHealth.isHealthy = false;
                return false;
            }

            const duration = Date.now() - startTime;
            _connectionHealth.lastCheck = Date.now();
            _connectionHealth.isHealthy = true;
            _connectionHealth.consecutiveFailures = 0;

            if (duration > 2000) {
                console.warn(`🐌 Slow Supabase health check: ${duration}ms`);
            } else {
                console.log(`✅ Supabase health check passed: ${duration}ms`);
            }

            return true;
        } catch (queryError) {
            clearTimeout(timeoutId);
            throw queryError;
        }
    } catch (error) {
        console.error('❌ Supabase health check exception:', error);
        _connectionHealth.isHealthy = false;
        return false;
    }
};

// **🚀 ENHANCED QUERY OPERATIONS**
export const performOptimizedQuery = async <T>(
    queryFn: () => Promise<{ data: T | null; error: any }>,
    context: string = 'query'
): Promise<T | null> => {
    return retryOperation(async () => {
        const startTime = Date.now();

        const result = await queryFn();

        const duration = Date.now() - startTime;

        if (result.error) {
            console.error(`❌ Supabase query error [${context}]:`, result.error.message);
            throw result.error;
        }

        // Log slow queries
        if (duration > 2000) {
            console.warn(`🐌 Slow Supabase query [${context}]: ${duration}ms`);
        } else if (duration > 5000) {
            console.error(`🚨 Very slow Supabase query [${context}]: ${duration}ms`);
        }

        return result.data;
    }, context);
};

// **🚀 OPTIMIZED TOKEN OPERATIONS**
export const getNewTokens = async (limit: number = 50): Promise<Token[]> => {
    const client = supabase;
    if (!client) {
        console.error('❌ Supabase client not available');
        return [];
    }

    return await performOptimizedQuery(async () => {
        // Use timeout with controller instead of abortSignal method
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), QUERY_TIMEOUT);

        try {
            const result = await client
                .from('new_tokens')
                .select('*')
                .order('created_at', { ascending: false })
                .limit(limit);

            clearTimeout(timeoutId);
            return result;
        } catch (error) {
            clearTimeout(timeoutId);
            throw error;
        }
    }, 'getNewTokens') || [];
};

export const searchNewTokens = async (searchTerm: string, limit: number = 25): Promise<Token[]> => {
    const client = supabase;
    if (!client) {
        console.error('❌ Supabase client not available');
        return [];
    }

    if (!searchTerm || searchTerm.trim() === '') {
        return await getNewTokens(limit);
    }

    return await performOptimizedQuery(async () => {
        const term = searchTerm.toLowerCase().trim();
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), QUERY_TIMEOUT);

        try {
            // First try exact mint match
            if (term.length > 30) { // Likely a mint address
                const exactResult = await client
                    .from('new_tokens')
                    .select('*')
                    .eq('mint', term)
                    .limit(1);

                if (exactResult.data && exactResult.data.length > 0) {
                    clearTimeout(timeoutId);
                    return exactResult;
                }
            }

            // Then do fuzzy search on name and symbol
            const result = await client
                .from('new_tokens')
                .select('*')
                .or(`name.ilike.%${term}%,symbol.ilike.%${term}%`)
                .order('created_at', { ascending: false })
                .limit(limit);

            clearTimeout(timeoutId);
            return result;
        } catch (error) {
            clearTimeout(timeoutId);
            throw error;
        }
    }, `searchNewTokens:${searchTerm}`) || [];
};

export const getTrendingTokens = async (limit: number = 25): Promise<Token[]> => {
    const client = supabase;
    if (!client) {
        console.error('❌ Supabase client not available');
        return [];
    }

    return await performOptimizedQuery(async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), QUERY_TIMEOUT);

        try {
            // Calculate 24 hours ago timestamp
            const twentyFourHoursAgo = new Date();
            twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);

            const result = await client
                .from('new_tokens')
                .select('*')
                .gte('curve_percentage', 30)
                .lt('curve_percentage', 100)
                .gte('created_at', twentyFourHoursAgo.toISOString())
                .gte('buys_count', 10)
                .order('curve_percentage', { ascending: false })
                .order('created_at', { ascending: false })
                .limit(limit);

            clearTimeout(timeoutId);
            return result;
        } catch (error) {
            clearTimeout(timeoutId);
            throw error;
        }
    }, 'getTrendingTokens') || [];
};

export const getMigratedTokens = async (limit: number = 25): Promise<Token[]> => {
    const client = supabase;
    if (!client) {
        console.error('❌ Supabase client not available');
        return [];
    }

    return await performOptimizedQuery(async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), QUERY_TIMEOUT);

        try {
            const result = await client
                .from('new_tokens')
                .select('*')
                .eq('curve_percentage', 100)
                .order('created_at', { ascending: false })
                .limit(limit);

            clearTimeout(timeoutId);
            return result;
        } catch (error) {
            clearTimeout(timeoutId);
            throw error;
        }
    }, 'getMigratedTokens') || [];
};

export const getGraduatingTokens = async (limit: number = 25): Promise<Token[]> => {
    const client = supabase;
    if (!client) {
        console.error('❌ Supabase client not available');
        return [];
    }

    // Try graduating_tokens table first, fallback to new_tokens with high curve_percentage
    return await performOptimizedQuery(async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), QUERY_TIMEOUT);

        try {
            // First try graduating_tokens table if it exists
            const graduatingResult = await client
                .from('graduating_tokens')
                .select('*')
                .order('created_at', { ascending: false })
                .limit(limit);

            if (graduatingResult.data && graduatingResult.data.length > 0) {
                clearTimeout(timeoutId);
                return graduatingResult;
            }

            // Fallback to new_tokens with high curve percentage
            const result = await client
                .from('new_tokens')
                .select('*')
                .gte('curve_percentage', 80)
                .lt('curve_percentage', 100)
                .order('curve_percentage', { ascending: false })
                .order('created_at', { ascending: false })
                .limit(limit);

            clearTimeout(timeoutId);
            return result;
        } catch (error) {
            clearTimeout(timeoutId);
            throw error;
        }
    }, 'getGraduatingTokens') || [];
};

export const getGraduatedTokens = async (limit: number = 25): Promise<Token[]> => {
    const client = supabase;
    if (!client) {
        console.error('❌ Supabase client not available');
        return [];
    }

    // Try graduated_tokens table first, fallback to new_tokens with 100% curve
    return await performOptimizedQuery(async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), QUERY_TIMEOUT);

        try {
            // First try graduated_tokens table if it exists
            const graduatedResult = await client
                .from('graduated_tokens')
                .select('*')
                .order('created_at', { ascending: false })
                .limit(limit);

            if (graduatedResult.data && graduatedResult.data.length > 0) {
                clearTimeout(timeoutId);
                return graduatedResult;
            }

            // Fallback to new_tokens with 100% curve (same as migrated)
            const result = await client
                .from('new_tokens')
                .select('*')
                .eq('curve_percentage', 100)
                .order('created_at', { ascending: false })
                .limit(limit);

            clearTimeout(timeoutId);
            return result;
        } catch (error) {
            clearTimeout(timeoutId);
            throw error;
        }
    }, 'getGraduatedTokens') || [];
};

export const getTokenByMint = async (mint: string): Promise<Token | null> => {
    const client = supabase;
    if (!client) {
        console.error('❌ Supabase client not available');
        return null;
    }

    return await performOptimizedQuery(async () => {
        // Use timeout with controller instead of abortSignal method
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), QUERY_TIMEOUT);

        try {
            const result = await client
                .from('new_tokens')
                .select('*')
                .eq('mint', mint)
                .single();

            clearTimeout(timeoutId);
            return result;
        } catch (error) {
            clearTimeout(timeoutId);
            throw error;
        }
    }, `getTokenByMint:${mint}`);
};

// **🔍 CONNECTION STATUS MONITORING**
export const getConnectionStatus = () => ({
    isHealthy: _connectionHealth.isHealthy,
    consecutiveFailures: _connectionHealth.consecutiveFailures,
    lastCheck: _connectionHealth.lastCheck,
    supabaseUrl: supabaseUrl
});

// **🚀 AUTOMATIC HEALTH MONITORING**
// Check connection health every 2 minutes
setInterval(async () => {
    if (isSupabaseConfigured) {
        await checkConnectionHealth();
    }
}, 120000); // 2 minutes

console.log('🚀 High-performance Supabase service initialized with:');
console.log(`   - Original URL: ${supabaseUrl}`);
console.log(`   - Query timeout: ${QUERY_TIMEOUT}ms`);
console.log(`   - Max retries: ${MAX_RETRIES}`);
console.log(`   - Connection pool size: ${CONNECTION_POOL_SIZE}`);
console.log(`   - Realtime throttle: ${REALTIME_THROTTLE} events/sec`);

export default supabase;